import React, { useState, useRef, useEffect } from 'react';
import { Shield, Cookie, Check, X, Settings, Eye, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

interface ConsentManagerProps {
  onConsentComplete: () => void;
}

export const ConsentManager: React.FC<ConsentManagerProps> = ({ onConsentComplete }) => {
  const { t } = useTranslation();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState<ConsentPreferences>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    personalization: false,
  });

  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if user has already provided consent
    const storedTermsConsent = localStorage.getItem('manivo_terms_accepted');
    const storedCookieConsent = localStorage.getItem('manivo_cookie_consent');
    
    console.log('ConsentManager checking localStorage:', { storedTermsConsent, storedCookieConsent });
    
    if (storedTermsConsent && storedCookieConsent) {
      console.log('Both consents found, calling onConsentComplete');
      onConsentComplete();
      return;
    }

    // Show terms modal first if not accepted
    if (!storedTermsConsent) {
      console.log('No terms consent, showing terms modal');
      setShowTermsModal(true);
    } else {
      setTermsAccepted(true);
      // If terms already accepted, show cookie modal
      if (!storedCookieConsent) {
        console.log('Terms accepted but no cookie consent, showing cookie modal');
        setShowCookieModal(true);
      }
    }
  }, [onConsentComplete]);

  // Reset state when component mounts (useful when user logs out and back in)
  useEffect(() => {
    setShowTermsModal(false);
    setShowCookieModal(false);
    setShowCookieSettings(false);
    setTermsAccepted(false);
    setCookiesAccepted(false);
  }, []);

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    localStorage.setItem('manivo_terms_accepted', 'true');
    localStorage.setItem('manivo_terms_accepted_date', new Date().toISOString());
    
    // Show cookie modal after terms acceptance
    const storedCookieConsent = localStorage.getItem('manivo_cookie_consent');
    if (!storedCookieConsent) {
      setShowCookieModal(true);
    } else {
      onConsentComplete();
    }
  };

  const handleCookieAcceptAll = () => {
    const allAcceptedPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    
    setCookiePreferences(allAcceptedPreferences);
    setCookiesAccepted(true);
    setShowCookieModal(false);
    
    localStorage.setItem('manivo_cookie_consent', JSON.stringify(allAcceptedPreferences));
    localStorage.setItem('manivo_cookie_consent_date', new Date().toISOString());
    
    onConsentComplete();
  };

  const handleCookieRejectAll = () => {
    const necessaryOnlyPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
    
    setCookiePreferences(necessaryOnlyPreferences);
    setCookiesAccepted(true);
    setShowCookieModal(false);
    
    localStorage.setItem('manivo_cookie_consent', JSON.stringify(necessaryOnlyPreferences));
    localStorage.setItem('manivo_cookie_consent_date', new Date().toISOString());
    
    onConsentComplete();
  };

  const handleCookieCustomSave = () => {
    setCookiesAccepted(true);
    setShowCookieSettings(false);
    setShowCookieModal(false);
    
    localStorage.setItem('manivo_cookie_consent', JSON.stringify(cookiePreferences));
    localStorage.setItem('manivo_cookie_consent_date', new Date().toISOString());
    
    onConsentComplete();
  };

  const updateCookiePreference = (key: keyof ConsentPreferences, value: boolean) => {
    setCookiePreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Terms & Conditions Modal
  if (showTermsModal) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden my-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 sm:p-6">
            <div className="flex items-center space-x-3">
              <Shield size={24} className="hidden sm:block" />
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">{t('consent.welcomeTitle')}</h2>
                <p className="text-purple-100 text-xs sm:text-sm">{t('consent.welcomeSubtitle')}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <Shield size={18} className="text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-800 text-sm sm:text-base mb-1 sm:mb-2">{t('consent.importantLegalAgreement')}</h3>
                    <p className="text-amber-700 text-xs sm:text-sm leading-relaxed">
                      {t('consent.legalAgreementText')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="prose text-xs sm:text-sm text-gray-700 space-y-3 sm:space-y-4">
                <h4 className="font-semibold text-gray-900">{t('consent.empowerYourJourney')}</h4>
                <ul className="space-y-1 sm:space-y-2">
                  <li>✓ {t('consent.empowerPoint1')}</li>
                  <li>✓ {t('consent.empowerPoint2')}</li>
                  <li>✓ {t('consent.empowerPoint3')}</li>
                  <li>✓ {t('consent.empowerPoint4')}</li>
                  <li>✓ {t('consent.empowerPoint5')}</li>
                </ul>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                  <p className="text-purple-800 font-medium text-xs sm:text-sm">
                    <strong>{t('consent.yourPowerYourResults')}</strong> {t('consent.powerResultsText')}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:gap-4 pt-2 sm:pt-4">
                <Link 
                  to="/terms" 
                  target="_blank"
                  className="text-purple-600 hover:text-purple-700 text-xs sm:text-sm font-medium flex items-center space-x-1"
                >
                  <Eye size={14} />
                  <span>{t('consent.readFullTerms')}</span>
                </Link>
                <Link 
                  to="/privacy" 
                  target="_blank"
                  className="text-purple-600 hover:text-purple-700 text-xs sm:text-sm font-medium flex items-center space-x-1"
                >
                  <Lock size={14} />
                  <span>{t('consent.privacyPolicy')}</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-4 sm:p-6 border-t">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleTermsAccept}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <Check size={16} />
                <span>{t('consent.acceptTerms')}</span>
              </button>
              <button
                onClick={() => window.history.back()}
                className="sm:w-auto bg-gray-200 text-gray-700 px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 text-sm sm:text-base"
              >
                {t('consent.leaveSite')}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              {t('consent.acceptTermsNote')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Cookie Settings Modal
  if (showCookieSettings) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden my-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6">
            <div className="flex items-center space-x-3">
              <Settings size={24} className="hidden sm:block" />
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">{t('consent.cookiePreferences')}</h2>
                <p className="text-blue-100 text-xs sm:text-sm">{t('consent.customizePrivacySettings')}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
            <div className="space-y-4 sm:space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">{t('consent.necessaryCookies')}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t('consent.necessaryCookiesDescription')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('consent.necessaryCookiesDetails')}
                  </p>
                </div>
                <div className="ml-2 sm:ml-4 flex-shrink-0">
                  <div className="w-10 sm:w-12 h-5 sm:h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                    <div className="w-3 sm:w-4 h-3 sm:h-4 bg-white rounded-full"></div>
                  </div>
                  <p className="text-xs text-green-600 mt-1 text-center">{t('consent.required')}</p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">{t('consent.analyticsCookies')}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t('consent.analyticsCookiesDescription')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('consent.analyticsCookiesDetails')}
                  </p>
                </div>
                <div className="ml-2 sm:ml-4 flex-shrink-0">
                  <button
                    onClick={() => updateCookiePreference('analytics', !cookiePreferences.analytics)}
                    className={`w-10 sm:w-12 h-5 sm:h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                      cookiePreferences.analytics ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="w-3 sm:w-4 h-3 sm:h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">{t('consent.marketingCookies')}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t('consent.marketingCookiesDescription')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('consent.marketingCookiesDetails')}
                  </p>
                </div>
                <div className="ml-2 sm:ml-4 flex-shrink-0">
                  <button
                    onClick={() => updateCookiePreference('marketing', !cookiePreferences.marketing)}
                    className={`w-10 sm:w-12 h-5 sm:h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                      cookiePreferences.marketing ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="w-3 sm:w-4 h-3 sm:h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              {/* Personalization Cookies */}
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">{t('consent.personalizationCookies')}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t('consent.personalizationCookiesDescription')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('consent.personalizationCookiesDetails')}
                  </p>
                </div>
                <div className="ml-2 sm:ml-4 flex-shrink-0">
                  <button
                    onClick={() => updateCookiePreference('personalization', !cookiePreferences.personalization)}
                    className={`w-10 sm:w-12 h-5 sm:h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                      cookiePreferences.personalization ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="w-3 sm:w-4 h-3 sm:h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-4 sm:p-6 border-t">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCookieCustomSave}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 text-sm sm:text-base"
              >
                {t('consent.savePreferences')}
              </button>
              <button
                onClick={() => setShowCookieSettings(false)}
                className="sm:w-auto bg-gray-200 text-gray-700 px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 text-sm sm:text-base"
              >
                {t('consent.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cookie Consent Banner
  if (showCookieModal) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-2xl w-full">
          {/* Header */}
          <div className="p-4 sm:p-6 border-b">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <Cookie size={20} className="text-amber-600" />
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('consent.cookiePreferences')}</h2>
            </div>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              {t('consent.cookieDescription')}
            </p>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Shield size={16} className="text-green-600 sm:text-green-600 sm:w-5 sm:h-5" />
                </div>
                <p className="text-xs font-medium text-gray-900">{t('consent.essential')}</p>
                <p className="text-xs text-gray-500">{t('consent.alwaysActive')}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Eye size={16} className="text-blue-600 sm:w-5 sm:h-5" />
                </div>
                <p className="text-xs font-medium text-gray-900">{t('consent.analytics')}</p>
                <p className="text-xs text-gray-500">{t('consent.optional')}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Settings size={16} className="text-purple-600 sm:w-5 sm:h-5" />
                </div>
                <p className="text-xs font-medium text-gray-900">{t('consent.marketing')}</p>
                <p className="text-xs text-gray-500">{t('consent.optional')}</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Check size={16} className="text-pink-600 sm:w-5 sm:h-5" />
                </div>
                <p className="text-xs font-medium text-gray-900">{t('consent.personal')}</p>
                <p className="text-xs text-gray-500">{t('consent.optional')}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleCookieAcceptAll}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 text-xs sm:text-sm"
              >
                {t('consent.acceptAllCookies')}
              </button>
              <button
                onClick={() => setShowCookieSettings(true)}
                className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors duration-200 text-xs sm:text-sm"
              >
                {t('consent.customize')}
              </button>
              <button
                onClick={handleCookieRejectAll}
                className="flex-1 bg-gray-200 text-gray-600 px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 text-xs sm:text-sm"
              >
                {t('consent.essentialOnly')}
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              {t('consent.changePreferencesNote')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};